import React, { useEffect, useRef } from "react";
import { DEFAULT_PIC } from "./constants";

// =====================================================
// CALL UI
// Full-screen overlay for the active/incoming call driven entirely by the
// useWebRTCCall hook's state. Renders:
//   • incoming "ringing" prompt (accept / decline)
//   • outgoing "calling…" state
//   • connected call — video tiles (video calls) or an avatar (voice calls),
//     with mute / camera / hang-up controls.
// Purely presentational: it attaches the streams to <video>/<audio> elements
// and calls back into the hook for every action.
// =====================================================

function CallUI({
  call,
  localStream,
  remoteStream,
  muted,
  cameraOff,
  peerAvatar,
  onAccept,
  onReject,
  onEnd,
  onToggleMute,
  onToggleCamera,
}) {
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);

  // Re-attach on call.state too: the connected layout swaps which <video>
  // element the ref points at, and the stream reference itself hasn't changed,
  // so without call.state in the deps the new element would render blank.
  useEffect(() => {
    if (localVideoRef.current && localStream) {
      localVideoRef.current.srcObject = localStream;
    }
  }, [localStream, call.state]);

  // Audible ring while an incoming call is pending. Built with WebAudio so it
  // needs no asset file and stops the instant the call leaves "ringing".
  // Autoplay can be blocked before any user gesture on the page — we swallow
  // that rejection so the visual prompt still shows regardless.
  useEffect(() => {
    if (call.state !== "ringing") return;
    let ctx;
    let timer;
    let stopped = false;
    const beep = () => {
      if (stopped || !ctx || ctx.state === "closed") return;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = 480;
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.9);
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 1);
    };
    const startRinging = () => {
      beep();
      timer = setInterval(beep, 2500);
    };
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      ctx = new AudioCtx();
      if (ctx.state === "suspended") {
        ctx.resume().then(startRinging).catch(() => {});
      } else {
        startRinging();
      }
    } catch {
      /* WebAudio/autoplay unavailable — visual ring still shows */
    }
    return () => {
      stopped = true;
      clearInterval(timer);
      if (ctx) {
        try {
          ctx.close();
        } catch {
          /* ignore */
        }
      }
    };
  }, [call.state]);

  useEffect(() => {
    const videoCarriesAudio =
      call.type === "video" && call.state === "connected";
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
    // Avoid double audio: when the remote <video> is on screen it already
    // plays the audio track, so the standalone <audio> stays empty then.
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = videoCarriesAudio ? null : remoteStream;
    }
  }, [remoteStream, call.state, call.type]);

  if (call.state === "idle") return null;

  const isVideo = call.type === "video";
  const isIncoming = call.state === "ringing";
  const connected = call.state === "connected";
  const statusText = call.error
    ? call.error
    : call.state === "calling"
    ? "Calling…"
    : call.state === "ringing"
    ? `Incoming ${isVideo ? "video" : "voice"} call`
    : call.state === "connecting"
    ? "Connecting…"
    : "Connected";

  return (
    <div className="call-overlay">
      {/* Remote audio always mounted so voice calls have sound. */}
      <audio ref={remoteAudioRef} autoPlay playsInline />

      <div className={`call-stage ${isVideo && connected ? "call-video" : ""}`}>
        {isVideo && connected ? (
          <>
            <video
              ref={remoteVideoRef}
              className="call-remote-video"
              autoPlay
              playsInline
            />
            <video
              ref={localVideoRef}
              className="call-local-video"
              autoPlay
              playsInline
              muted
            />
          </>
        ) : (
          <div className="call-avatar-wrap">
            <img
              className="call-avatar"
              src={peerAvatar || DEFAULT_PIC}
              alt=""
            />
            <h2 className="call-peer-name">{call.peerName}</h2>
            <p className={`call-status ${call.error ? "call-error" : ""}`}>
              {statusText}
            </p>
            {/* Keep a hidden local video mounted for video calls pre-connect
                so the stream is attached and permission stays live. */}
            {isVideo && (
              <video
                ref={localVideoRef}
                style={{ display: "none" }}
                autoPlay
                playsInline
                muted
              />
            )}
          </div>
        )}

        {isVideo && connected && (
          <div className="call-video-status">
            <span>{call.peerName}</span>
            <small className={call.error ? "call-error" : ""}>{statusText}</small>
          </div>
        )}
      </div>

      <div className="call-controls">
        {isIncoming ? (
          <>
            <button className="call-btn call-decline" onClick={onReject}>
              📞 Decline
            </button>
            <button className="call-btn call-accept" onClick={onAccept}>
              📞 Accept
            </button>
          </>
        ) : (
          <>
            <button
              className={`call-btn call-toggle ${muted ? "active" : ""}`}
              onClick={onToggleMute}
              title={muted ? "Unmute" : "Mute"}
            >
              {muted ? "🔇" : "🎙"}
            </button>

            {isVideo && (
              <button
                className={`call-btn call-toggle ${cameraOff ? "active" : ""}`}
                onClick={onToggleCamera}
                title={cameraOff ? "Turn camera on" : "Turn camera off"}
              >
                {cameraOff ? "📷̶" : "📹"}
              </button>
            )}

            <button className="call-btn call-end" onClick={onEnd} title="End call">
              📞 End
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export default CallUI;
